import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CmElement, CmHelp, CmSection, ContentType } from '../shared/model/cm/managed-content.model';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { UserSessionService } from '../shared/service/user-session.service';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { FormUtils } from '../shared/helper/form-utils';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
@Component({
  selector: 'app-consent',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './consent.component.html',
  styleUrls: ['./consent.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConsentComponent implements OnInit {
  @Output() cancelEmitter = new EventEmitter<void>();
  checkboxFormValid: boolean = true;

  public readonly pageIllustrationUrl = 'assets/Illustration-Footer-Base-Mountains.svg';

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public companyId: string;

  // Page content from drupal
  consentContent: CmSection;

  // FormControl metadata from drupal for consent field
  consentElement: CmElement;

  // Form definition and alias
  consentForm: FormGroup;

  get consent() { 
    if(this.consentForm) {
      return this.consentForm.get("consent"); 
    }
  }

  // Distilled page content
  public consentTitle: string;
  public consentBody: string;
  public consentPrimaryButton: string;

  // GA4 Custom events
  public displayConsent = {
    action: "button_click",
    category: "popup_window",
    name: "display_consent",
  }

  public submitConsent = {
    action: "button_click",
    category: "submit_consent_popup",
    name: "submit_consent",
  }

  public cancelConsent = {
    action: "button_click",
    category: "cancel_consent_popup",
    name: "cancel_consent",
  }

  // Other
  consentSubmitted: boolean = false;

  private log(content: string) {
    this.appInsightsLoggerService.log('info', 'Info log from consent.component', content);
  }

  constructor(
    private router: Router,
    private cmService: ManagedContentService,
    private userSessionService: UserSessionService,
    private appInsightsLoggerService: AppInsightsLoggerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private pagetitleService: PagetitleService,
    private authGuardService: AuthGuardService,
    private modalService: ModalService,
    private windowService: WindowRef,
    private fb: FormBuilder,
    public formControlUtilitiesService: FormControlUtilitiesService,
  ) {}


  ngOnInit(): void {

    this.consentForm = new FormGroup({
      consent: new FormControl(false, [Validators.requiredTrue])
    });

    this.companyId = this.userSessionService.data.companyId;

    if(this.userSessionService.data.policy == undefined){
      if(this.authGuardService){
        this.authGuardService.cancelLeaveAnyway();
      }
      this.router.navigate(['/instructions']);
    }

    if(this.pagetitleService){
      this.pagetitleService.setPageTitle('Consent');
    }
    
    this.googleAnalyticsService.sendCustomEvent(this.displayConsent);

    this.consentContent = this.cmService.getContentById(ContentType.MODAL, "electronic-terms-consent");
    this.consentElement = this.cmService.getContentById(ContentType.ELEMENT, "consent");

    if(this.userSessionService.data.companyId === "YI"){
      this.consentContent = this.cmService.getContentById(ContentType.MODAL, "electronic-terms-consent-sny");
      this.consentElement = this.cmService.getContentById(ContentType.ELEMENT, "consent-sny");
    }
    if(this.userSessionService.data.companyId === "NM"){
      this.consentContent = this.cmService.getContentById(ContentType.MODAL, "electronic-terms-consent-nm");
      this.consentElement = this.cmService.getContentById(ContentType.ELEMENT, "consent-nm");
      this.consentPrimaryButton = "Let’s get started";
    }
    this.consentTitle = this.consentContent.heading;
    this.consentBody = this.consentContent.body;
    this.consentElement=this.consentElement;
  }

  onSubmit(){
    this.googleAnalyticsService.sendCustomEvent(this.submitConsent);
    if(this.consentForm){
      this.consentForm.markAllAsTouched();
      this.consentForm.updateValueAndValidity();
      this.formControlUtilitiesService.setCheckboxFormValid(this.consentForm);
      this.consentSubmitted = true;
      if(this.consentForm.valid){
        this.userSessionService.data.consent = this.consent.value;
        if (this.userSessionService.data.mhsApplication) {
          this.userSessionService.data.mhsApplication.consented = this.userSessionService.data.consent;
        }
        this.router.navigate(['/state-of-residence']);
      }
    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancel(){
    this.googleAnalyticsService.sendCustomEvent(this.cancelConsent);

    this.consentSubmitted = false;
    
    this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
  }

  onCancelModal(modalName: string){
    this.cancelEmitter.emit();
    
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from Consent.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }
}